import { Children, isValidElement, useLayoutEffect, useRef, type ReactElement, type ReactNode } from "react";

import { cn } from "@shared/lib/cn";

/*
 * Меры набора v6 (`radio` в `liquid-ui.js`): кольцо, кегль, высота строки.
 */
const SIZES = {
  s: [18, 14, 34],
  m: [22, 15, 42],
  l: [26, 17, 50],
} as const;

const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

interface ItemProps {
  value: string;
  title: ReactNode;
  /** Пояснение под подписью: чем ответ отличается от соседних. */
  note?: ReactNode;
  disabled?: boolean;
  /** Ответа в продукте ещё нет: строка погашена и помечена. */
  plan?: boolean;
}

/** Ответ в группе. Сам себя не рисует: его раскладывает `RadioGroup`. */
function RadioItem(props: ItemProps): ReactElement | null {
  void props;
  return null;
}

/**
 * Радио по набору v6.
 *
 * Капля акцента на всю группу одна: выбрали другой ответ — она перетекает к
 * его кольцу пружиной (520/32) и вытягивается вдоль пути на скорости, а не
 * гаснет в одном кольце и загорается в другом. Под указателем едет светлая
 * плашка; нажатое кольцо сжимается.
 *
 * Значение держит хозяин. Стрелки ходят по доступным ответам, фокус — у
 * выбранного (как у обычной группы переключателей в браузере).
 */
function RadioGroup({
  value,
  onValueChange,
  size = "s",
  className,
  children,
  "aria-label": label,
}: {
  value: string;
  onValueChange?: (next: string) => void;
  size?: keyof typeof SIZES;
  className?: string;
  children?: ReactNode;
  "aria-label"?: string;
}) {
  const [D, FS, RH] = SIZES[size];
  const items = Children.toArray(children).filter(
    (child): child is ReactElement<ItemProps> => isValidElement(child) && child.type === RadioItem,
  );
  const sel = Math.max(0, items.findIndex((item) => item.props.value === value));
  const off = (i: number) => !!(items[i]?.props.disabled || items[i]?.props.plan);

  const root = useRef<HTMLDivElement>(null);
  const plate = useRef<HTMLSpanElement>(null);
  const dot = useRef<HTMLSpanElement>(null);
  const opts = useRef<(HTMLDivElement | null)[]>([]);
  const rings = useRef<(HTMLSpanElement | null)[]>([]);
  const motion = useRef<{ go: (snap?: boolean) => void } | null>(null);
  const selRef = useRef(sel);

  // Капля: положение и скорость живут вне React — они меняются каждый кадр.
  useLayoutEffect(() => {
    const at = (i: number): [number, number] => {
      const o = opts.current[i];
      const r = rings.current[i];
      if (!o || !r) return [0, 0];
      return [o.offsetLeft + r.offsetLeft + D / 2, o.offsetTop + r.offsetTop + D / 2];
    };
    let P = at(selRef.current);
    let V: [number, number] = [0, 0];
    let T = P.slice() as [number, number];
    let raf = 0;
    let last = 0;

    const draw = () => {
      const sp = Math.hypot(V[0], V[1]);
      const s = Math.min(0.6, sp * 0.0016);
      const a = (Math.atan2(V[1], V[0]) * 180) / Math.PI;
      dot.current!.style.transform =
        `translate(${P[0].toFixed(2)}px,${P[1].toFixed(2)}px) rotate(${a.toFixed(1)}deg) ` +
        `scale(${(1 + s).toFixed(3)},${(1 - s * 0.38).toFixed(3)}) rotate(${(-a).toFixed(1)}deg)`;
    };
    const step = (now: number) => {
      const dt = Math.min(1 / 30, last ? (now - last) / 1000 : 1 / 60);
      last = now;
      let busy = false;
      for (let k = 0; k < 2; k++) {
        const acc = (T[k] - P[k]) * 520 - V[k] * 32;
        V[k] += acc * dt;
        P[k] += V[k] * dt;
        if (Math.abs(T[k] - P[k]) > 0.15 || Math.abs(V[k]) > 2) busy = true;
      }
      if (!busy) {
        P = T.slice() as [number, number];
        V = [0, 0];
      }
      draw();
      raf = busy ? requestAnimationFrame(step) : 0;
      if (!raf) last = 0;
    };
    const go = (snap = false) => {
      T = at(selRef.current);
      if (snap || reduced()) {
        P = T.slice() as [number, number];
        V = [0, 0];
        draw();
        return;
      }
      if (!raf) raf = requestAnimationFrame(step);
    };
    motion.current = { go };
    go(true);

    // Строки поменяли высоту (переносы, шрифт) — капля встаёт к кольцу сразу, без езды.
    const ro = new ResizeObserver(() => go(true));
    ro.observe(root.current!);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      motion.current = null;
    };
  }, [D]);

  useLayoutEffect(() => {
    if (selRef.current === sel) return;
    selRef.current = sel;
    motion.current?.go();
  }, [sel]);

  /*
   * Плашка под указателем: первый раз встаёт на место без езды (иначе
   * выезжала бы из угла), дальше едет к строке под курсором.
   */
  const plateTo = (o: HTMLElement) => {
    const p = plate.current!;
    const hidden = p.style.opacity !== "1";
    if (hidden) p.style.transition = "opacity .15s ease";
    p.style.transform = `translate(${o.offsetLeft}px,${o.offsetTop}px)`;
    p.style.width = o.offsetWidth + "px";
    p.style.height = o.offsetHeight + "px";
    p.style.opacity = "1";
    if (hidden) {
      void p.offsetWidth;
      p.style.transition = "";
    }
  };

  const pick = (i: number, focus: boolean) => {
    if (i < 0 || i >= items.length || off(i)) return;
    if (focus) opts.current[i]?.focus({ preventScroll: true });
    if (i !== sel) onValueChange?.(items[i].props.value);
  };

  const next = (i: number, d: number) => {
    for (let k = 1; k <= items.length; k++) {
      const n = (i + d * k + items.length * 4) % items.length;
      if (!off(n)) return n;
    }
    return i;
  };

  return (
    <div
      ref={root}
      role="radiogroup"
      aria-label={label}
      className={cn("lui-radio", className)}
      onPointerLeave={() => {
        if (plate.current) plate.current.style.opacity = "0";
      }}
    >
      <span ref={plate} className="lui-radio-plate" aria-hidden />
      {items.map((item, i) => {
        const { title, note, plan } = item.props;
        const muted = off(i);
        const ring = rings;
        return (
          <div
            key={item.props.value}
            ref={(node) => {
              opts.current[i] = node;
            }}
            role="radio"
            aria-checked={i === sel}
            aria-disabled={muted || undefined}
            tabIndex={i === sel ? 0 : -1}
            className={cn("lui-radio-opt", note != null && "with-note", muted && "muted-row")}
            style={{ minHeight: RH, fontSize: FS }}
            onPointerEnter={(e) => plateTo(e.currentTarget)}
            onPointerDown={() => ring.current[i]?.style.setProperty("transform", "scale(.86)")}
            onPointerUp={() => ring.current[i]?.style.removeProperty("transform")}
            onPointerLeave={() => ring.current[i]?.style.removeProperty("transform")}
            onPointerCancel={() => ring.current[i]?.style.removeProperty("transform")}
            onClick={() => pick(i, true)}
            onKeyDown={(e) => {
              const k = e.key;
              if (k === "ArrowDown" || k === "ArrowRight") {
                e.preventDefault();
                pick(next(i, 1), true);
              } else if (k === "ArrowUp" || k === "ArrowLeft") {
                e.preventDefault();
                pick(next(i, -1), true);
              } else if (k === " " || k === "Enter") {
                e.preventDefault();
                pick(i, true);
              }
            }}
          >
            <span
              ref={(node) => {
                rings.current[i] = node;
              }}
              className="lui-radio-ring"
              // С пояснением кольцо стоит по первой строке подписи, а не по середине строки.
              style={{ width: D, height: D, marginTop: note != null ? Math.max(0, (Math.round(FS * 1.3) - D) / 2) : 0 }}
            />
            <span className="lui-radio-text">
              <span className="lui-radio-title">
                {title}
                {plan && <span className="plan-tag">план</span>}
              </span>
              {note != null && (
                <span className="lui-radio-note" style={{ fontSize: FS - 2 }}>
                  {note}
                </span>
              )}
            </span>
          </div>
        );
      })}
      <span
        ref={dot}
        className="lui-radio-dot"
        style={{ width: D, height: D, margin: `${-D / 2}px 0 0 ${-D / 2}px` }}
        aria-hidden
      >
        <span className="lui-radio-pip" style={{ width: Math.round(D * 0.38), height: Math.round(D * 0.38) }} />
      </span>
    </div>
  );
}

export { RadioGroup, RadioItem };
